import React, { useState, useCallback } from 'react'
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core'
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import Step1 from './step-1'
import Step2 from './step-2'
import Step3 from './step-3'
import { provinceList } from './address-data'
import _ from 'lodash'
import { useMutation } from 'react-query'
import CustomSnackbar from '../custom-snackbar'
import { buildPrivacyRecord, PRIVACY_API_BASE_URL } from './privacy-settings'

const schemaStep1 = yup.object().shape({
  activeCollaboration: yup
    .string()
    .oneOf(['yes', 'no'], 'Seleziona Sì oppure No')
    .required('Indica se intendi collaborare attivamente nel MRF'),
  name: yup
    .string()
    .min(2, 'Minimo 2 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required('Nome richiesto'),
  surname: yup
    .string()
    .min(2, 'Minimo 2 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required("Cognome richiesto"),
  birthDate: yup
    .date()
    .typeError("Inserisci una data di nascita valida")
    .required("Data di nascita richiesto"),
  gender: yup
    .string()
    .oneOf(['M', 'F', 'O'], 'Seleziona il sesso')
    .required('Seleziona il sesso'),
  birthCity: yup
    .string()
    .min(2, 'Minimo 2 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required("Citta di nascita richiesto"),
  birthRegion: yup
    .string()
    .oneOf(_.uniq(provinceList.map(provincia => provincia.regione)), "Seleziona la regione di nascita")
    .required("Regione di nascita richiesto"),
  birthProvince: yup
    .string()
    .oneOf(_.uniq(provinceList.map(provincia => provincia.sigla)), "Seleziona la provincia di nascita")
    .required("Provincia di nascita richiesto"),
  residenceMunicipality: yup
    .string()
    .min(2, 'Minimo 2 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required("Comune di residenza richiesto"),
  CAP: yup
    .string()
    .length(5, "Il Cap deve essere di 5 caratteri")
    .matches(/^[0-9]*$/, "Il Cap deve essere composto da soli numeri")
    .required("CAP richiesto"),
  profession: yup
    .string()
    .min(2, 'Minimo 2 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required("Professione richiesto"),
}).required();

const schemaStep2 = yup.object().shape({
  phone: yup
    .string()
    .min(10, 'Minimo 10 caratteri')
    .max(20, 'Massimo 20 caratteri')
    .required('Telefono richiesto'),
  email: yup
    .string()
    .email("Inserire Email valida")
    .required("Email richiesto"),
  password: yup
    .string()
    .min(6, 'Minimo 6 caratteri')
    .max(50, 'Massimo 50 caratteri')
    .required("Password richiesto"),
}).required();

const schema = schemaStep1.concat(schemaStep2);

export default ({  }) => {
  const [step, setStep] = useState(0)
  const [privacyManagementUrl, setPrivacyManagementUrl] = useState('')
  const [privacyConsent, setPrivacyConsent] = useState(false)
  const [communicationsConsent, setCommunicationsConsent] = useState(false)

  const useStyles = makeStyles(() => ({
    rootPaper: {
      padding: '1.5rem 2rem 2rem',
      borderRadius: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      '@media (max-width: 600px)': { padding: '1rem' }
    }
  }))

  const classes = useStyles()

  const { register, handleSubmit, errors, control, watch, setValue } = useForm({
    defaultValues: {
      birthDate: null,
      activeCollaboration: ''
    },
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const restURI = PRIVACY_API_BASE_URL

  const [documentInfo, setDocumentInfo] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [isReadingDocument, setIsReadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');

  const formValues = watch();
  const isComplete = Boolean(schema.isValidSync(formValues) && privacyConsent && documentInfo && documentData && !isReadingDocument);

  const fieldLabels = {
    name: 'Nome', surname: 'Cognome', birthDate: 'Data di nascita', gender: 'Sesso',
    birthCity: 'Città di nascita', birthRegion: 'Regione di nascita', birthProvince: 'Provincia di nascita',
    residenceMunicipality: 'Comune di residenza', CAP: 'CAP (5 cifre)', profession: 'Professione',
    activeCollaboration: 'Risposta sulla collaborazione attiva', phone: 'Telefono (almeno 10 caratteri)',
    email: 'Email valida', password: 'Password (almeno 6 caratteri)'
  };
  const missingFields = Object.keys(fieldLabels)
    .filter(name => !schema.fields[name].isValidSync(formValues[name]))
    .map(name => fieldLabels[name]);
  if (!documentInfo || !documentData) missingFields.push(isReadingDocument ? 'Documento in caricamento' : 'Documento di riconoscimento');
  if (!privacyConsent) missingFields.push('Consenso per esaminare la domanda di adesione');

  // SNACK BAR ERROR
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [message, setMessage] = useState("");

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenErrorSnackbar(false);
  };


  const { isLoading, mutate } = useMutation('postUsers', ({ data }, ) =>
    fetch(`${restURI}/users`, {
      method: 'post',
      body: JSON.stringify(data)
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const resp = await res.json();
          throw new Error(resp.message)
        }
        return res;
      })
      .then(res => res.json()),
    {
      onSuccess: (data) => {
        const url = data && data.privacyManagementUrl;
        if (typeof url === 'string' && /^https:\/\/(?:www|staging)\.movimentorivoluzionefiscale\.it\/gestisci-consensi\/#token=[a-f0-9]{64}$/.test(url)) setPrivacyManagementUrl(url);
        setStep(2);
      },
      onError: (e) => {
        setMessage(e.message);
        setOpenErrorSnackbar(true);
      }
    }
  );

  const onSubmit = useCallback(
    (_data) => {
      if (!isComplete || isLoading || step === 2) return;
      mutate({
        data: {
          ..._data,
          documentData,
          documentInfo,
          privacy: buildPrivacyRecord(privacyConsent, communicationsConsent),
        },
      });
    },
    [isComplete, isLoading, step, mutate, documentData, documentInfo, privacyConsent, communicationsConsent]
  );

  const onDocumentChange = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    setDocumentInfo(null);
    setDocumentData(null);
    setDocumentError('');
    if (!file) return;

    setIsReadingDocument(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Non è stato possibile leggere il documento. Riprova.'));
        reader.readAsDataURL(file);
      });
      setDocumentData(base64);
      setDocumentInfo({ name: file.name, size: file.size, type: file.type });
    } catch (error) {
      setDocumentError(error.message);
    } finally {
      setIsReadingDocument(false);
    }
  }, [])

  return (
      <Grid item xs={12}>
        <Paper classes={{
          root: classes.rootPaper
        }}>
          <Grid item xs={12}>
            <CustomSnackbar open={openErrorSnackbar} handleClose={handleClose} severity={'error'} message={`Errore nella richiesta: ${message}`}/>
            <form autoComplete="on" hidden={step === 2} noValidate onSubmit={handleSubmit(onSubmit)}>
              <Typography component="p" style={{ marginBottom: 16 }}>Compila i dati anagrafici e i recapiti, allega il documento e conferma il consenso per esaminare la domanda. Non occorre firmare l’informativa privacy. Il consenso agli aggiornamenti via email è facoltativo. Il pulsante diventa verde quando tutti i dati richiesti sono completi e validi.</Typography>
              <Step1 register={register} errors={errors} control={control} watch={watch} setValue={setValue} onDocumentChange={onDocumentChange} isReadingDocument={isReadingDocument} documentError={documentError} />
              <Step2 register={register} errors={errors} isLoading={isLoading} privacyConsent={privacyConsent} onPrivacyChange={setPrivacyConsent} communicationsConsent={communicationsConsent} onCommunicationsChange={setCommunicationsConsent} isComplete={isComplete} missingFields={missingFields} />
            </form>
            <Step3 step={step} privacyManagementUrl={privacyManagementUrl} />
          </Grid>
        </Paper>
      </Grid>
  )
}
